import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import { BanlistCardStatus, Nullable, UserWithToken, WithData } from './api.types';
import { MetaDeckTier } from './metaDecks';
import { CardUsageTier, DecklistMatchConfidence, DeckZone } from './tournamentDecklists';

/** El período analizado. Con banlist, `since`/`until` son su fecha efectiva y la de la siguiente. */
export type MetaWindow = {
  formatId: number;
  banlistId: number | null;
  banlistName: string | null;
  since: string | null;
  until: string | null;
};

export type MetaTierEntry = {
  metaDeckId: number;
  name: string;
  coverKonamiId: number | null;
  coverSquareUrl: string | null;
  count: number;
  /** count / listas con mazo identificado, de 0 a 1. */
  share: number;
  /** Puesto (8/6/4/3/2/1) × log2(max(jugadores, 8)) / 3, sumado. */
  points: number;
  pointsShare: number;
  wins: number;
  tier: MetaDeckTier;
};

export type MetaTierListSide = {
  decklistCount: number;
  unmatchedCount: number;
  totalPoints: number;
  decks: MetaTierEntry[];
};

export type LocalMetaCoverage = {
  tournamentCount: number;
  playerCount: number;
  withMetaDeckCount: number;
  share: number;
};

export type MetaTierList = {
  window: MetaWindow;
  global: MetaTierListSide;
  argentina: MetaTierListSide;
  coverage: LocalMetaCoverage;
};

export type MetaWindowFilters = {
  formatId?: number;
  banlistId?: number;
  /** ISO. */
  since?: string;
  /** ISO, exclusivo. */
  until?: string;
};

export type MetaCardPresence = {
  code: number;
  name: string;
  deckCount: number;
  share: number;
  avgCopies: number;
  metaDeckCount: number;
  sideDeckCount: number;
  sideShare: number;
  sideAvgCopies: number;
  genesysPoints: number;
  banlistStatus: BanlistCardStatus | null;
};

export type MetaCardPresenceResult = {
  window: MetaWindow;
  decklistCount: number;
  metaDeckCount: number;
  baseBanlistId: number | null;
  baseBanlistName: string | null;
  cards: MetaCardPresence[];
};

export type MetaCardPresenceFilters = Omit<MetaWindowFilters, 'until'> & {
  maxPlacementRank?: number;
  /** Default 100, máximo 500. */
  limit?: number;
};

export type MetaDeckMissingCard = {
  code: number;
  name: string;
  deckType: DeckZone;
  tier: CardUsageTier;
  share: number;
  targetCopies: number;
  ownedCopies: number;
  missingCopies: number;
  availableCopies: number;
  minPrice: number | null;
  estimatedCost: number;
  uncoveredCopies: number;
  purchases: MetaMissingPurchase[];
};

/** Una publicación elegida para cubrir lo que falta (la más barata primero): lo que pide el carrito. */
export type MetaMissingPurchase = {
  listingId: number;
  sellerId: number;
  sellerName: string;
  setCode: string | null;
  setName: string | null;
  rarity: string | null;
  language: string | null;
  condition: string | null;
  quantity: number;
  stock: number;
  price: number;
};

export type MetaDeckMissingCards = {
  metaDeckId: number;
  formatId: number;
  banlistId: number | null;
  decklistCount: number;
  /** 'ARS'. */
  currency: string;
  totalTargetCopies: number;
  totalOwnedCopies: number;
  totalMissingCopies: number;
  estimatedCost: number;
  uncoveredCopies: number;
  cards: MetaDeckMissingCard[];
};

export type DecklistCardInput = { code: number; deckType: DeckZone; quantity: number };

export type MetaDeckCardDiff = {
  code: number;
  name: string;
  deckType: DeckZone;
  tier: CardUsageTier;
  share: number;
  modeCopies: number;
  avgCopies: number;
  copies: number;
};

export type MetaOffMetaCard = { code: number; name: string; deckType: DeckZone; copies: number; share: number };

export type MetaZoneSizeDiff = { zone: DeckZone; copies: number; average: number; diff: number };

export type MetaDecklistComparison = {
  metaDeckId: number | null;
  metaDeckName: string | null;
  detected: boolean;
  confidence: DecklistMatchConfidence | null;
  formatId: number;
  banlistId: number | null;
  decklistCount: number;
  missing: MetaDeckCardDiff[];
  fewerCopies: MetaDeckCardDiff[];
  moreCopies: MetaDeckCardDiff[];
  offMeta: MetaOffMetaCard[];
  sizes: MetaZoneSizeDiff[];
};

const META_WINDOW_FIELDS = `
  window { formatId banlistId banlistName since until }
`;

const TIER_SIDE_FIELDS = `
  decklistCount
  unmatchedCount
  totalPoints
  decks {
    metaDeckId
    name
    coverKonamiId
    coverSquareUrl
    count
    share
    points
    pointsShare
    wins
    tier
  }
`;

const CARD_DIFF_FIELDS = `
  code
  name
  deckType
  tier
  share
  modeCopies
  avgCopies
  copies
`;

/** Pública. Tier list de afuera (YGOProDeck) y de nuestros torneos en la misma ventana. */
export const getPublicMetaTierList = async (user: Nullable<UserWithToken>, filters: MetaWindowFilters = {}) =>
  getGraphQLQueryClient(user).request<WithData<MetaTierList>>(
    gql`
      query publicMetaTierList($formatId: Int, $banlistId: Int, $since: DateTime, $until: DateTime) {
        data: publicMetaTierList(formatId: $formatId, banlistId: $banlistId, since: $since, until: $until) {
          ${META_WINDOW_FIELDS}
          global { ${TIER_SIDE_FIELDS} }
          argentina { ${TIER_SIDE_FIELDS} }
          coverage { tournamentCount playerCount withMetaDeckCount share }
        }
      }
    `,
    filters
  );

/** Pública. Presencia de cada carta en todas las listas legales del período (predictor de banlist). */
export const getPublicMetaCardPresence = async (
  user: Nullable<UserWithToken>,
  filters: MetaCardPresenceFilters = {}
) =>
  getGraphQLQueryClient(user).request<WithData<MetaCardPresenceResult>>(
    gql`
      query publicMetaCardPresence(
        $formatId: Int
        $banlistId: Int
        $since: DateTime
        $maxPlacementRank: Int
        $limit: Int
      ) {
        data: publicMetaCardPresence(
          formatId: $formatId
          banlistId: $banlistId
          since: $since
          maxPlacementRank: $maxPlacementRank
          limit: $limit
        ) {
          ${META_WINDOW_FIELDS}
          decklistCount
          metaDeckCount
          baseBanlistId
          baseBanlistName
          cards {
            code
            name
            deckCount
            share
            avgCopies
            metaDeckCount
            sideDeckCount
            sideShare
            sideAvgCopies
            genesysPoints
            banlistStatus
          }
        }
      }
    `,
    filters
  );

/** Con sesión. Lo que le falta al usuario para armar el MetaDeck, con la oferta del marketplace. */
export const getMyMetaDeckMissingCards = async (
  user: Nullable<UserWithToken>,
  metaDeckId: number,
  options: { formatId?: number; tiers?: CardUsageTier[] } = {}
) =>
  getGraphQLQueryClient(user).request<WithData<MetaDeckMissingCards>>(
    gql`
      query myMetaDeckMissingCards($metaDeckId: Int!, $formatId: Int, $tiers: [CardUsageTier!]) {
        data: myMetaDeckMissingCards(metaDeckId: $metaDeckId, formatId: $formatId, tiers: $tiers) {
          metaDeckId
          formatId
          banlistId
          decklistCount
          currency
          totalTargetCopies
          totalOwnedCopies
          totalMissingCopies
          estimatedCost
          uncoveredCopies
          cards {
            code
            name
            deckType
            tier
            share
            targetCopies
            ownedCopies
            missingCopies
            availableCopies
            minPrice
            estimatedCost
            uncoveredCopies
            purchases {
              listingId
              sellerId
              sellerName
              setCode
              setName
              rarity
              language
              condition
              quantity
              stock
              price
            }
          }
        }
      }
    `,
    { metaDeckId, ...options }
  );

/** Pública. Sin `metaDeckId`, el mazo se detecta de la lista. */
export const comparePublicDecklistToMeta = async (
  user: Nullable<UserWithToken>,
  cards: DecklistCardInput[],
  options: { metaDeckId?: number; formatId?: number } = {}
) =>
  getGraphQLQueryClient(user).request<WithData<MetaDecklistComparison>>(
    gql`
      query publicCompareDecklistToMeta($cards: [DecklistCardInput!]!, $metaDeckId: Int, $formatId: Int) {
        data: publicCompareDecklistToMeta(cards: $cards, metaDeckId: $metaDeckId, formatId: $formatId) {
          metaDeckId
          metaDeckName
          detected
          confidence
          formatId
          banlistId
          decklistCount
          missing { ${CARD_DIFF_FIELDS} }
          fewerCopies { ${CARD_DIFF_FIELDS} }
          moreCopies { ${CARD_DIFF_FIELDS} }
          offMeta { code name deckType copies share }
          sizes { zone copies average diff }
        }
      }
    `,
    { cards, ...options }
  );
